import React, { useEffect, useState, useCallback } from 'react';
import api from '@/utils/api';
import { DollarSign, Store, ChevronLeft, ChevronRight, CheckCircle, XCircle, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { useTenant } from '@/contexts/TenantContext';

const IMPACT_GOLF_TENANT_ID = 'tenant_2a7782078b27';

const STAGE_LABEL = { 1: 'Cost Payout', 2: 'Profit Share' };

export default function AdminPayouts() {
  const { tenant } = useTenant();
  const isImpactGolf = tenant?.tenant_id === IMPACT_GOLF_TENANT_ID;
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalReceived, setTotalReceived] = useState(0);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 20;

  const loadPayouts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/payouts', { params: { page, limit } });
      setPayouts(response.data.payouts || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.total_pages || 1);
      setTotalReceived(response.data.total_received || 0);
    } catch (error) {
      toast.error('Failed to load payouts');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    loadPayouts();
  }, [loadPayouts]);

  if (loading && payouts.length === 0) {
    return (
      <div className="text-center py-12" data-testid="payouts-loading">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-[#334155] border-r-transparent"></div>
        <p className="mt-4 text-gray-600">Loading payouts...</p>
      </div>
    );
  }

  return (
    <div data-testid="admin-payouts-page">
      <h2 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
        Payout Summary
      </h2>
      <p className="text-sm text-gray-500 mb-8">
        Every Stripe transfer tied to your fulfillment orders, in one place — no need to open Stripe for a quick check.
      </p>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
        <div className="bg-gray-50 p-4 rounded-lg border border-[#F3F4F6]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider">Total Received</p>
              <p className="text-2xl font-bold text-[#4A6741]" data-testid="payouts-total-received">${totalReceived.toFixed(2)}</p>
            </div>
            <DollarSign className="h-8 w-8 text-[#4A6741]" />
          </div>
        </div>
        <div className="bg-gray-50 p-4 rounded-lg border border-[#F3F4F6]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wider">Total Transfers</p>
              <p className="text-2xl font-bold text-gray-900" data-testid="payouts-total-count">{total}</p>
            </div>
            <ExternalLink className="h-8 w-8 text-[#334155]" />
          </div>
        </div>
      </div>

      {payouts.length === 0 ? (
        <div className="text-center py-16 bg-gray-50 rounded-lg border border-[#F3F4F6]" data-testid="payouts-empty">
          <DollarSign className="h-10 w-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-600 font-medium">No payouts yet</p>
          <p className="text-sm text-gray-400 mt-1">
            Payouts appear here once fulfillment orders are paid and shipped.
          </p>
        </div>
      ) : (
        <div className="overflow-x-auto border border-[#F3F4F6] rounded-lg">
          <table className="w-full text-sm" data-testid="payouts-table">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Order</th>
                {isImpactGolf && <th className="text-left px-4 py-3">Store</th>}
                <th className="text-left px-4 py-3">Type</th>
                <th className="text-left px-4 py-3">Stage</th>
                <th className="text-right px-4 py-3">Amount</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F3F4F6]">
              {payouts.map((p, idx) => (
                <tr key={`${p.order_id}-${p.role}-${idx}`} data-testid={`payout-row-${p.order_id}-${p.role}`}>
                  <td className="px-4 py-3 font-mono text-xs text-gray-700">{p.order_id}</td>
                  {isImpactGolf && (
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1 text-xs bg-[#4A6741]/10 text-[#4A6741] px-2 py-0.5 rounded font-medium">
                        <Store className="h-3 w-3" />
                        {p.store_name || 'ImpactGolf'}
                      </span>
                    </td>
                  )}
                  <td className="px-4 py-3 text-gray-700">{p.role_label}</td>
                  <td className="px-4 py-3 text-gray-500">{STAGE_LABEL[p.stage] || p.stage}</td>
                  <td className="px-4 py-3 text-right font-bold text-gray-900">
                    {p.amount != null ? `$${Number(p.amount).toFixed(2)}` : '—'}
                  </td>
                  <td className="px-4 py-3">
                    {p.status === 'succeeded' ? (
                      <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded font-medium">
                        <CheckCircle className="h-3 w-3" /> Succeeded
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center gap-1 text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded font-medium"
                        title={p.error || 'Transfer failed'}
                      >
                        <XCircle className="h-3 w-3" /> Failed
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">
                    {p.executed_at ? new Date(p.executed_at).toLocaleString() : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Pagination */}
      {!loading && payouts.length > 0 && totalPages > 1 && (
        <div className="flex items-center justify-between mt-6" data-testid="payouts-pagination">
          <p className="text-sm text-gray-500">Page {page} of {totalPages}</p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-2 rounded border border-[#F3F4F6] disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
              data-testid="payouts-prev-page"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-2 rounded border border-[#F3F4F6] disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50"
              data-testid="payouts-next-page"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
